import { getDeviceFingerprint } from "../../helpers/deviceFingerprint";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import authServiceInstance from "../../api/AuthApi";
import { notification } from "antd";
import { ROUTES_MAP } from "../../routes/routesMap";
import { IUser } from "../../types/models";
import { ILoginResponse } from "../../types/response";
import { TOKEN_KEY } from "../../constants/constants";
import { trackSignupCompleted } from "../../helpers/analytics";
import { dispatchErrorNotification } from "../../helpers/errorDispatch";
import { disconnectSocket } from "../../helpers/socket";
import i18n from "../../i18n";
import { invalidateSession } from "../../helpers/request";
import { configuredFrontendPath } from "../../routes/frontendBasePath";

interface InitialState {
  isLoading: boolean;
  restoreState:
    | "loading"
    | "authenticated"
    | "unauthorized"
    | "retryable_error";
  restoreRequestId?: string;
  user?: IUser;
  token: string;
  resetEmailSent: boolean;
  passwordUpdated: boolean;
  verifyUserLoading: boolean;
  resendLoading: boolean;
}

const storedToken = localStorage.getItem(TOKEN_KEY);

const initialState: InitialState = {
  user: undefined,
  token: storedToken || "",
  isLoading: false,
  restoreState: "loading",
  resetEmailSent: false,
  passwordUpdated: false,
  verifyUserLoading: false,
  resendLoading: false,
};

export type AuthRestoreOutcome =
  | { state: "authenticated"; user: IUser }
  | { state: "unauthorized" }
  | { state: "retryable_error" };

export const getMe = createAsyncThunk<AuthRestoreOutcome>(
  "auth/getMe",
  async (_, { getState, requestId }) => {
    try {
      const response = await authServiceInstance.getMe(true);
      return { state: "authenticated", user: response.data };
    } catch (error: any) {
      if (error?.status === 401) {
        const auth = (getState() as { auth: InitialState }).auth;
        if (auth.restoreRequestId === requestId) {
          disconnectSocket();
          invalidateSession();
        }
        return { state: "unauthorized" };
      }
      return { state: "retryable_error" };
    }
  }
);

export const AuthSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setVerifyLoading(state, action: PayloadAction<boolean>) {
      state.verifyUserLoading = action.payload;
    },
    setResendLoading(state, action: PayloadAction<boolean>) {
      state.resendLoading = action.payload;
    },
    onLoginSuccess(state, action: PayloadAction<ILoginResponse>) {
      const { user, token } = action.payload;
      state.restoreRequestId = undefined;
      state.restoreState = "authenticated";
      state.user = user;
      state.token = token;
      localStorage.setItem(TOKEN_KEY, token);
      const lastLocation = window.localStorage.getItem("lastLocation");
      window.localStorage.removeItem("lastLocation");
      if (
        lastLocation &&
        lastLocation.startsWith("/") &&
        !lastLocation.includes("as-user")
      ) {
        window.location.href = configuredFrontendPath(lastLocation);
      } else {
        window.location.href = configuredFrontendPath(
          ROUTES_MAP.dashboard.liveStreams
        );
      }
    },
    onGetMeSuccess(state, action: PayloadAction<IUser>) {
      state.user = action.payload;
      state.restoreState = "authenticated";
    },
    logoutUser(state) {
      state.restoreRequestId = undefined;
      state.restoreState = "unauthorized";
      disconnectSocket();
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem("lastLocation");
      state.token = "";
      state.user = undefined;
      window.location.href = configuredFrontendPath(ROUTES_MAP.auth.login);
    },
    setResetEmailSent(state, action: PayloadAction<boolean>) {
      state.resetEmailSent = action.payload;
    },
    setPasswordUpdated(state, action: PayloadAction<boolean>) {
      state.passwordUpdated = action.payload;
    },
    setToken(state, action: PayloadAction<string>) {
      state.restoreRequestId = undefined;
      state.restoreState = "loading";
      state.token = action.payload;
      localStorage.setItem(TOKEN_KEY, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMe.pending, (state, action) => {
      state.restoreRequestId = action.meta.requestId;
      if (!state.user) state.restoreState = "loading";
      state.isLoading = true;
    });
    builder.addCase(getMe.fulfilled, (state, action) => {
      if (state.restoreRequestId !== action.meta.requestId) return;
      state.restoreRequestId = undefined;
      state.isLoading = false;
      if (action.payload.state === "retryable_error" && state.user) {
        state.restoreState = "authenticated";
        return;
      }
      state.restoreState = action.payload.state;
      if (action.payload.state === "authenticated")
        state.user = action.payload.user;
      if (action.payload.state === "unauthorized") {
        state.user = undefined;
        state.token = "";
      }
    });
  },
});

export const {
  setLoading,
  onLoginSuccess,
  logoutUser,
  setResetEmailSent,
  setPasswordUpdated,
  onGetMeSuccess,
  setVerifyLoading,
  setResendLoading,
  setToken,
} = AuthSlice.actions;

export const login = createAsyncThunk(
  "auth/login",
  async (
    { email, password }: { email: string; password: string },
    { rejectWithValue, dispatch }
  ) => {
    dispatch(setLoading(true));
    try {
      const response = await authServiceInstance.login({ email, password });
      const data = response.data;
      dispatch(onLoginSuccess(data));
    } catch (err: any) {
      dispatchErrorNotification(err);
    }

    dispatch(setLoading(false));
  }
);

export const register = createAsyncThunk(
  "auth/register",
  async (
    data: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      country: string;
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      utm_term?: string;
      utm_content?: string;
      referrer?: string;
      landing_page?: string;
    },
    { dispatch }
  ) => {
    dispatch(setLoading(true));
    try {
      const deviceFingerprint = await getDeviceFingerprint();
      const response = await authServiceInstance.signup({ ...data, ...(deviceFingerprint ? { deviceFingerprint } : {}) });
      trackSignupCompleted();
      dispatch(onLoginSuccess(response.data));
    } catch (err: any) {
      console.log({ err });
      dispatchErrorNotification(err);
    }
    dispatch(setLoading(false));
  }
);

export const requestResetPassword = createAsyncThunk(
  "auth/requestResetPassword",
  async (data: { email: string }, { dispatch }) => {
    dispatch(setLoading(true));
    try {
      const response = await authServiceInstance.requestResetPassword(data);
      dispatch(setResetEmailSent(true));
    } catch (err: any) {
      console.log({ err });
      dispatchErrorNotification(err);
    }
    dispatch(setLoading(false));
  }
);

export const submitResetPassword = createAsyncThunk(
  "auth/submitResetPassword",
  async (data: { token: string; password: string }, { dispatch }) => {
    dispatch(setLoading(true));
    try {
      await authServiceInstance.submitResetPassword(data);
      notification.success({
        message: i18n.t("auth:notifications.passwordUpdated"),
      });
      dispatch(setPasswordUpdated(true));
    } catch (err: any) {
      console.log({ err });
      dispatchErrorNotification(err);
    }
    dispatch(setLoading(false));
  }
);

export const verifyUser = createAsyncThunk(
  "auth/verifyUser",
  async (data: { code: string }, { dispatch }) => {
    dispatch(setVerifyLoading(true));
    try {
      await authServiceInstance.submitVerifyAccount(data);
      await dispatch(getMe());
      notification.success({
        message: i18n.t("auth:notifications.accountVerified"),
      });
    } catch (err: any) {
      console.log({ err });
      dispatchErrorNotification(err);
    }
    dispatch(setVerifyLoading(false));
  }
);

/** Resolves true when the email was sent. A24: false after a failed send, so
 * the modal can offer Resend again at once. */
export const resendVerifyUser = createAsyncThunk<boolean>(
  "auth/resendVerifyUser",
  async (_, { dispatch }) => {
    dispatch(setResendLoading(true));
    try {
      await authServiceInstance.resendVerifyAccount();
      notification.success({ message: i18n.t("auth:notifications.emailSent") });
      return true;
    } catch (err: any) {
      dispatchErrorNotification(err);
      return false;
    } finally {
      dispatch(setResendLoading(false));
    }
  }
);

export const impersonateUser = createAsyncThunk(
  "auth/impersonateUser",
  async (_, { dispatch }) => {
    dispatch(setLoading(true));
    try {
      const response = await authServiceInstance.getMe();
      dispatch(onGetMeSuccess(response.data));
      window.location.href = configuredFrontendPath(
        ROUTES_MAP.dashboard.liveStreams
      );
    } catch (err: any) {
      console.log({ err });
      dispatchErrorNotification(err);
    }
    dispatch(setLoading(false));
  }
);
